/**
 * Sidebar navigation: label, path, allowed roles.
 *
 * Tartib prinsipi: rol bo‘yicha `*_SIDEBAR_ORDER` bilan; Ega va bosh/viloyat menejer / dispecher uchun
 * ayrim bo‘limlar (ombor, filiallar, reyting, moliya, maslahat, sessiyalar va h.k.) cheklangan.
 */

import type { Role } from './roles';
import type { PlanCapabilitiesSnapshot } from '../types/planCapabilities';
import { marketingOnlineAddonNavVisible } from '../utils/marketingOnlineAddonGate';

function pathAllowedByPlan(path: string, planCaps: PlanCapabilitiesSnapshot | null | undefined): boolean {
  if (path === 'marketing' || path === 'marketing-orders') {
    if (!planCaps?.features) return true;
    const f = planCaps.features;
    if (f.marketing_tools && f.online_orders) return true;
    return marketingOnlineAddonNavVisible(planCaps);
  }
  if (!planCaps?.features) return true;
  const f = planCaps.features;
  if (path === 'ai-chat' && !f.ai_assistant) return false;
  if (path === 'pos-access') {
    const max = planCaps.max_pos_access ?? 0;
    return max > 0;
  }
  return true;
}

export interface SidebarItem {
  label: string;
  path: string;
  allowedRoles: Role[];
  children?: SidebarItem[];
  labelKey?: string;
  /** Rol bo'yicha boshqa tarjima kaliti (masalan dispecher menyusida «Murojaatlar») */
  labelKeyByRole?: Partial<Record<Role, string>>;
  /** Ko'rinmasdan saqlash — menyuda chiqmasligi, lekin marshrut va logika saqlanadi */
  hidden?: boolean;
}

const EGA: Role = 'platform_owner';
const BOSH_MENEJER: Role = 'general_manager';
const VILOYAT_MENEJER: Role = 'regional_manager';
const DISPECHER: Role = 'dispatcher';
const MIJOZ: Role = 'tenant_owner';
const KASSIR: Role = 'cashier';
/** Sozlamalar — Ega, bosh/viloyat menejer va dispatcher menyusida yo‘q */
const SETTINGS_ROLES: Role[] = [MIJOZ];
/** Yordam — faqat operativ rollar; menejerlar/platform_owner chat (tenant_owner xabarlari) orqali */
const HELP_ROLES: Role[] = [MIJOZ];
/** Sessiyalar — bosh menejer, viloyat menejer va dispecher menyusida yo‘q */
const SESSIONS_ROLES: Role[] = [EGA, MIJOZ, KASSIR];

function nav(path: string, label: string, allowedRoles: Role[]): SidebarItem {
  return { label, path, allowedRoles, labelKey: 'adminNav_' + path.replace(/-/g, '_') };
}

export const SIDEBAR_CONFIG: SidebarItem[] = [
  // —— Yuqorida: Statistika; AI chat — Ega, bosh menejer, mijoz (tenant_owner, tarif bo‘yicha) ——
  nav('statistics', 'Statistics', [EGA, BOSH_MENEJER, VILOYAT_MENEJER, DISPECHER, MIJOZ]),
  nav('tariff-plan', 'Tarif rejasi', []),
  /** AI chat — platform (ega/bosh menejer) + mijoz roli (label: AI yordamchi). Viloyat/dispecher/kassirda yo‘q. */
  { ...nav('ai-chat', 'Consultation', [EGA, BOSH_MENEJER, MIJOZ]), labelKeyByRole: { tenant_owner: 'adminNav_ai_helper' } },

  // —— Ega: mijozlar, menejerlar, dispecherlar, buyurtmalar (ombor/filiallar/reyting/moliya alohida yo‘q) ——
  nav('system-dashboard', 'System Dashboard', [EGA]),
  /** Pastdagi rollardagi akkaunt sifatida kirish — faqat navbar (Topbar) tugmasi orqali; chap menyuda ko‘rinmaydi */
  { ...nav('role-access', 'Rollarga kirish', [EGA, BOSH_MENEJER, VILOYAT_MENEJER, MIJOZ]), hidden: true },
  { ...nav('ai-advisor', 'AI Advisor', [EGA, BOSH_MENEJER, MIJOZ]), hidden: true },
  nav('tariff-plans', 'Tariff Plans', [EGA]),
  nav('landing-team', 'Landing Team', [EGA]),
  /** Integratsiya / audit / tizim holati / foydalanish / xatoliklar — Ega menyusida yo‘q. */
  nav('tenant-management', 'Mijozlar', [EGA, BOSH_MENEJER, VILOYAT_MENEJER]),
  nav('tenant-messages', 'Chat', [EGA, BOSH_MENEJER, VILOYAT_MENEJER]),
  { ...nav('cashier-chat', 'Kassirlar bilan chat', [MIJOZ, KASSIR]), labelKeyByRole: { cashier: 'adminNav_chat' } },
  nav('staff-chat', 'Ichki chat', [EGA, BOSH_MENEJER, VILOYAT_MENEJER, DISPECHER]),
  nav('branches', 'Filiallar', [MIJOZ]),
  nav('retail-clients', 'Chakana tenant_ownerlar', [MIJOZ, KASSIR]),
  { ...nav('feature-flags', 'Feature Flags', [EGA]), hidden: true },

  // —— Bosh menejer va Ega: menejerlar, dispatcherlar, buyurtmalar / hisobot ——
  nav('managers', 'Managers', [EGA, BOSH_MENEJER]),
  /** Dispecherlar CRUD — faqat Ega va bosh menejer (viloyat menejer ko‘rmaydi). */
  nav('dispatchers', 'Dispatchers', [EGA, BOSH_MENEJER]),
  nav('orders', 'Orders', [BOSH_MENEJER]),
  nav('reports', 'Reports', [BOSH_MENEJER]),

  // —— Viloyat menejer ——
  { ...nav('leads', 'Leads', [VILOYAT_MENEJER]), labelKeyByRole: { regional_manager: 'adminNav_leads_vm' } },
  nav('orders', 'Orders', [VILOYAT_MENEJER]),

  // —— Dispecher: mijoz bo‘yicha murojaatlar (leadlar) — qidiruv, jaloba/taklif, viloyat menejerga. ——
  { ...nav('leads', 'Leads', [DISPECHER]), labelKeyByRole: { dispatcher: 'adminNav_leads_dispatcher' } },
  nav('orders', 'Orders', [DISPECHER]),

  // —— Omborchi, Mijoz (faqat inventory-dashboard — Ombor) ——
  nav('inventory-dashboard', 'Inventory Dashboard', [MIJOZ]),
  // Legacy omborchi bo'limlari: mijoz rolida ko'rinmasin.
  { ...nav('stock-in', 'Stock In', [EGA]), hidden: true },
  { ...nav('stock-out', 'Stock Out', [EGA]), hidden: true },
  { ...nav('transfers', 'Transfers', [EGA]), hidden: true },
  { ...nav('barcode-scanner', 'Barcode Scanner', [EGA]), hidden: true },
  { ...nav('inventory-audit', 'Inventory Audit', [EGA]), hidden: true },
  { ...nav('expiry-tracking', 'Expiry Tracking', [EGA]), hidden: true },

  // —— Buxgalter (Mijoz uchun moliya bo‘limlari olib tashlangan) ——
  nav('finance-dashboard', 'Finance Dashboard', []),
  {
    ...nav('transactions', 'Transactions', [MIJOZ]),
    labelKeyByRole: { tenant_owner: 'adminNav_manager_payments' },
  },
  /** Hisob-fakturalar — faqat buxgalter (Ega menyusida yo‘q). */
  nav('invoices', 'Invoices', []),
  { ...nav('taxes', 'Taxes', [MIJOZ]), hidden: true },
  nav('profit-loss', 'Profit & Loss', []),
  { ...nav('balance-sheet', 'Balance Sheet', [MIJOZ]), hidden: true },
  { ...nav('export-reports', 'Export Reports', [MIJOZ]), hidden: true },

  // —— Analitik (Mijoz uchun tahlil/funnel/segment/kampaniya/kohort olib tashlangan) ——
  // Legacy analitik bo'limlari: mijoz rolida ko'rinmasin.
  { ...nav('analytics-dashboard', 'Analytics Dashboard', [EGA]), hidden: true },
  { ...nav('sales-funnel', 'Sales Funnel', [EGA]), hidden: true },
  { ...nav('customer-segments', 'Customer Segments', [EGA]), hidden: true },
  { ...nav('campaigns', 'Campaigns', [EGA]), hidden: true },
  { ...nav('ai-recommendations', 'AI Recommendations', [EGA]), hidden: true },
  { ...nav('retention', 'Retention', [EGA]), hidden: true },
  { ...nav('cohort-analysis', 'Cohort Analysis', [EGA]), hidden: true },

  // —— Mijoz: Statistika, Ombor, Filiallar, Kassirlar, Ishchilar, Tranzaksiyalar, Hisobotlar, Maslahat, Sozlamalar, Yordam ——
  nav('cashiers', 'Cashiers', [MIJOZ]),
  nav('pos-access', 'POS access', [MIJOZ]),
  nav('owner-z-reports', 'Z-Reports', [MIJOZ]),
  nav('product-audit', 'Product audit', [MIJOZ]),
  nav('staff-workers', 'Staff / payroll', [MIJOZ]),
  nav('credits', 'Nasiya', [MIJOZ]),
  nav('manager-payments', 'Payments', [KASSIR]),
  nav('cashier-z-report', 'Z-Report', [KASSIR]),
  nav('expenses', 'Harajatlar', [MIJOZ]),
  nav('reports', 'Reports', [MIJOZ]),
  nav('cashier-rating', 'Kassirlar reytingi', [MIJOZ]),
  nav('marketing', 'Marketing', [MIJOZ, KASSIR]),
  nav('marketing-orders', 'Online buyurtmalar', [MIJOZ, KASSIR]),
  nav('desktop-apps', 'Ilovalar', [MIJOZ, KASSIR]),

  // —— Kassir: Kassa, Ombor (mahsulotlar), Tranzaksiyalar, Maslahat ——
  nav('kassa', 'Kassa', [KASSIR, MIJOZ]),
  nav('ombor-products', 'Ombor', [KASSIR]),
  nav('credits', 'Nasiya', [KASSIR]),
  // manager-payments kassir uchun yuqorida allaqachon qo'shilgan (dublikat bo'lmasin)
  nav('expenses', 'Harajatlar', [KASSIR]),
  nav('cashier-rating', 'Kassirlar reytingi', [KASSIR]),
  { ...nav('maslahat', 'Maslahat', [KASSIR, VILOYAT_MENEJER]), hidden: true },
  nav('printer-connect', 'Printerlarni ulash', []),

  // —— Sozlamalar / Yordam ——
  nav('sessions', 'Sessions', [EGA]),
  nav('settings', 'Settings', SETTINGS_ROLES),
  nav('help', 'Help', HELP_ROLES),
];

export function flattenSidebarItems(items: SidebarItem[], basePath = ''): { path: string; label: string }[] {
  const result: { path: string; label: string }[] = [];
  for (const item of items) {
    const fullPath = basePath ? `${basePath}/${item.path}` : item.path;
    result.push({ path: fullPath, label: item.label });
    if (item.children?.length) {
      result.push(...flattenSidebarItems(item.children, fullPath));
    }
  }
  return result;
}

/**
 * Ega (platform_owner): boshqaruv & KPI → mijozlar & tarif → jamoa (menejer / dispecher / ichki chat) →
 * bajariladigan operatsiya (ombor → filiallar) → reytinglar (jamoa va mijozlar bo‘yicha) →
 * moliya → AI maslahat → sozlamalar.
 */
const EGA_SIDEBAR_ORDER = [
  'system-dashboard',
  'statistics',
  'tenant-management',
  'tenant-messages',
  'tariff-plans',
  'landing-team',
  'managers',
  'dispatchers',
  'staff-chat',
  'ombor',
  'branches',
  'rating',
  'rating-dispatchers',
  'rating-tenants',
  'ai-chat',
  'sessions',
  'settings',
] as const;

/** Kassir: kassa va savdo oqimi birinchi, keyin moliyaviy, keyin texnik yordam */
const KASSIR_SIDEBAR_ORDER = [
  'kassa',
  'desktop-apps',
  'retail-clients',
  'ombor-products',
  'marketing',
  'marketing-orders',
  'cashier-chat',
  'cashier-rating',
  'credits',
  'manager-payments',
  'cashier-z-report',
  'expenses',
] as const;

/** Mijoz: statistika → ombor → POS → hisobot → moliya → marketing → AI → filial tarmog‘i → chakana → kassirlar → … → sozlamalar */
const MIJOZ_SIDEBAR_ORDER = [
  'statistics',
  'inventory-dashboard',
  'kassa',
  'reports',
  'transactions',
  'credits',
  'expenses',
  'marketing',
  'marketing-orders',
  'ai-chat',
  'branches',
  'retail-clients',
  'cashiers',
  'pos-access',
  'cashier-chat',
  'cashier-rating',
  'staff-workers',
  'owner-z-reports',
  'product-audit',
  'desktop-apps',
  'settings',
  'help',
] as const;

/** Viloyat menejer: viloyat KPI → tenant_ownerlar → operatsiya → jamoa → maslahat */
const VILOYAT_MENEJER_SIDEBAR_ORDER = [
  'statistics',
  'leads',
  'tenant-management',
  'orders',
  'tenant-messages',
  'staff-chat',
] as const;

/** Dispecher: voronka → bajarish → ichki aloqa → konsultatsiya */
const DISPECHER_SIDEBAR_ORDER = [
  'statistics',
  'leads',
  'orders',
  'staff-chat',
] as const;

/** Bosh menejer: KPI → tijorat → operatsiya / aloqa → boshqaruv → hisobot */
const BOSH_MENEJER_SIDEBAR_ORDER = [
  'statistics',
  'ai-chat',
  'tenant-management',
  'orders',
  'tenant-messages',
  'staff-chat',
  'managers',
  'dispatchers',
  'reports',
] as const;

const UNKNOWN_ORDER_INDEX = 999;

function sortSidebarByOrder(items: SidebarItem[], order: readonly string[]): SidebarItem[] {
  return [...items].sort((a, b) => {
    const i = order.indexOf(a.path);
    const j = order.indexOf(b.path);
    return (i === -1 ? UNKNOWN_ORDER_INDEX : i) - (j === -1 ? UNKNOWN_ORDER_INDEX : j);
  });
}

const POS_ONLY_CASHIER_ORDER = ['kassa'] as const;

export type SidebarFilterOptions = {
  /** Kassir: faqat POS kabina — boshqa menyu bandlari yashiriladi */
  posOnly?: boolean;
};

export function filterSidebarByRole(
  items: SidebarItem[],
  role: Role,
  planCaps?: PlanCapabilitiesSnapshot | null,
  options?: SidebarFilterOptions,
): SidebarItem[] {
  if (options?.posOnly && role === 'cashier') {
    return sortSidebarByOrder(
      items.filter((item) => !item.hidden && item.allowedRoles.includes(role) && item.path === 'kassa'),
      POS_ONLY_CASHIER_ORDER,
    );
  }

  const filtered = items
    .filter((item) => !item.hidden && item.allowedRoles.includes(role))
    .filter((item) => {
      if (role !== 'tenant_owner' && role !== 'cashier') return true;
      return pathAllowedByPlan(item.path, planCaps);
    })
    .map((item) => ({
      ...item,
      children: item.children?.length
        ? filterSidebarByRole(item.children, role, planCaps)
        : undefined,
    }))
    .filter((item) => !item.children || item.children.length > 0);

  const orderByRole: Record<Role, readonly string[]> = {
    platform_owner: EGA_SIDEBAR_ORDER,
    general_manager: BOSH_MENEJER_SIDEBAR_ORDER,
    regional_manager: VILOYAT_MENEJER_SIDEBAR_ORDER,
    dispatcher: DISPECHER_SIDEBAR_ORDER,
    tenant_owner: MIJOZ_SIDEBAR_ORDER,
    cashier: KASSIR_SIDEBAR_ORDER,
  };

  return sortSidebarByOrder(filtered, orderByRole[role]);
}

export function getDefaultPathForRole(
  role: Role,
  planCaps?: PlanCapabilitiesSnapshot | null,
  options?: SidebarFilterOptions,
): string {
  if (role === 'cashier') return 'kassa';
  if (role === 'tenant_owner') return 'statistics';
  const filtered = filterSidebarByRole(SIDEBAR_CONFIG, role, planCaps, options);
  const first = filtered[0];
  return first ? first.path : 'system-dashboard';
}

/** Yashirin (hidden) bandlar ham hisobga olinadi — legacy marshrut /admin/maslahat va boshqalar uchun */
export function canAccessPathIncludingHidden(role: Role, path: string): boolean {
  return SIDEBAR_CONFIG.some((item) => item.path === path && item.allowedRoles.includes(role));
}

/** Berilgan rol uchun path ruxsat etilganmi (Route Guard uchun) */
export function canAccessPath(
  role: Role,
  path: string,
  planCaps?: PlanCapabilitiesSnapshot | null,
  options?: SidebarFilterOptions,
): boolean {
  const filtered = filterSidebarByRole(SIDEBAR_CONFIG, role, planCaps, options);
  return filtered.some((item) => item.path === path);
}
